// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Section Layout - renders section report data. Receives all data via props
 * (no fetching).
 */
import React from 'react'
import { cn } from '@/components/ui'
import { SummaryCard } from './summary/SummaryCard'
import { SectionCard } from './section/components/SectionCard'
import type { ReportSection } from './section/types'

export type { ReportSection }

export interface SectionLayoutProps {
  headline?: string
  summary?: string
  score: number | null
  sections: ReportSection[]
  className?: string
}

export const SectionLayout: React.FC<SectionLayoutProps> = ({
  headline,
  summary,
  score,
  sections,
  className,
}) => {
  if (sections.length === 0) {
    return (
      <div className={cn('text-center text-sm text-muted-foreground py-8', className)}>
        No report data available.
      </div>
    )
  }

  const hasSummary = Boolean(headline || summary)

  return (
    <div className={cn('space-y-6', className)}>
      {hasSummary && (
        <SummaryCard
          title="Report Summary"
          headline={headline}
          summary={summary}
          score={score}
        />
      )}
      {sections.map((section) => (
        <SectionCard
          key={section.kbItemId}
          title={section.sectionLabel}
          content={section.content}
        />
      ))}
    </div>
  )
}
